import React, { useEffect, useRef, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';

// Landing stays eager — it is the LCP-critical entry. Everything else is
// route-split so the public bundle ships without the dashboards' weight
// (qrcode, editors, admin tables…). Core Web Vitals are a ranking factor.
import Landing from './pages/Landing.jsx';
import { SEO_PAGES, SEO_PAGE_KEYS } from './seo/content.js';
import { scrollToHash } from './components/v2/scrollToHash.js';
import { SCHED_ON, SCHED_PROTO } from './v2/sched/flag.js';

const Login = lazy(() => import('./pages/Login.jsx'));
const ResetPassword = lazy(() => import('./pages/ResetPassword.jsx'));
const SignUp = lazy(() => import('./pages/SignUp.jsx'));
const Dashboard = lazy(() => import('./pages/Dashboard.jsx'));
const Admin = lazy(() => import('./pages/Admin.jsx'));
const PublicCard = lazy(() => import('./pages/PublicCard.jsx'));
const Pricing = lazy(() => import('./pages/Pricing.jsx'));
const Business = lazy(() => import('./pages/Business.jsx'));
const BusinessDashboard = lazy(() => import('./pages/BusinessDashboard.jsx'));
const EmployeeOnboard = lazy(() => import('./pages/EmployeeOnboard.jsx'));
const EmployeePortal = lazy(() => import('./pages/EmployeePortal.jsx'));
const ScanCard = lazy(() => import('./pages/ScanCard.jsx'));
const GoogleWalletLab = lazy(() => import('./pages/GoogleWalletLab.jsx'));
const SeoLanding = lazy(() => import('./pages/SeoLanding.jsx'));
const MeetingBooking = lazy(() => import('./pages/MeetingBooking.jsx'));
const Comparison = lazy(() => import('./pages/Comparison.jsx'));
// Design prototype (mock data only), not linked from any page.
const SchedulingPrototype = SCHED_PROTO ? lazy(() => import('./pages/SchedulingPrototype.jsx')) : null;
// CardPass Scheduling (gated by SCHED_ON until launch; docs/scheduling/CONTRACT.md)
const IndividualScheduling = lazy(() => import('./pages/sched/IndividualScheduling.jsx'));
const BookingPublic = lazy(() => import('./pages/sched/BookingPublic.jsx'));
const BookingManage = lazy(() => import('./pages/sched/BookingManage.jsx'));
const TeamBookingPublic = lazy(() => import('./pages/sched/TeamBookingPublic.jsx'));
// The booking page inside a customer's website (public/embed.js)
const EmbedBooking = lazy(() => import('./pages/sched/EmbedBooking.jsx'));
const NotFound = lazy(() => import('./pages/NotFound.jsx'));
const Privacy = lazy(() => import('./pages/Legal.jsx').then((m) => ({ default: m.Privacy })));
const Terms = lazy(() => import('./pages/Legal.jsx').then((m) => ({ default: m.Terms })));
const CalendarIntegration = lazy(() => import('./pages/Legal.jsx').then((m) => ({ default: m.CalendarIntegration })));
// Dev-only V2 styleguide (/__v2). `import.meta.env.DEV` is statically false in
// production builds, so this branch and its chunk are dropped from dist/.
const V2Styleguide = import.meta.env.DEV ? lazy(() => import('./v2/_styleguide/Styleguide.jsx')) : null;

// Reset scroll to the top on every route change so /pricing, /business, etc.
// always open from the top instead of inheriting the previous page's scroll.
// A URL with a #hash (e.g. /#faq from another page's header) lands on that
// section instead, once the lazily-loaded page has rendered it.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const lastPath = useRef(null);
  useEffect(() => {
    if (lastPath.current !== pathname) window.scrollTo(0, 0);
    lastPath.current = pathname;
    // only section ids — auth redirects put tokens in the hash (#access_token=…)
    if (!/^#[A-Za-z][\w-]*$/.test(hash)) return undefined;
    let cancel = () => {};
    const raf = window.requestAnimationFrame(() => { cancel = scrollToHash(hash, { wait: 2500 }); });
    return () => { window.cancelAnimationFrame(raf); cancel(); };
  }, [pathname, hash]);
  return null;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const { pathname, search } = useLocation();
  if (loading) return <FullScreenSpinner />;
  if (!user) {
    // Send the visitor back here after sign-in (e.g. /scan from the landing page or a QR code); /app is Login's default.
    const next = pathname === '/app' ? '' : `?next=${encodeURIComponent(pathname + search)}`;
    return <Navigate to={`/login${next}`} replace />;
  }
  return children;
}

function FullScreenSpinner() {
  return (
    <div
      role="status"
      aria-label="Loading"
      style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg)' }}
    >
      <div className="v2-spin" />
    </div>
  );
}

export default function App() {
  const isBusinessHost =
    typeof window !== 'undefined' &&
    window.location.hostname.toLowerCase() === 'business.cardpass.co';

  if (isBusinessHost) {
    return (
      <>
        <ScrollToTop />
        <Suspense fallback={<FullScreenSpinner />}>
          <Routes>
            <Route path="*" element={<BusinessDashboard />} />
          </Routes>
        </Suspense>
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<FullScreenSpinner />}>
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/business" element={<Business />} />
      <Route path="/business-dashboard" element={<BusinessDashboard />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/calendar-integration" element={<CalendarIntegration />} />
      {/* SEO keyword landing pages — registered before the /:slug catch-all
          (their slugs are also in RESERVED_SLUGS so no card can shadow them) */}
      {SEO_PAGE_KEYS.map((key) => (
        <Route key={key} path={`/${key}`} element={key === 'meeting-booking' ? <MeetingBooking /> : SEO_PAGES[key].vs ? <Comparison pageKey={key} /> : <SeoLanding pageKey={key} />} />
      ))}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Admin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/scan"
        element={
          <ProtectedRoute>
            <ScanCard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/google-wallet-lab"
        element={
          <ProtectedRoute>
            {/* internal lab page keeps the old dark look (src/index.css "Legacy" section) */}
            <div className="legacy" style={{ minHeight: '100vh' }}>
              <GoogleWalletLab />
            </div>
          </ProtectedRoute>
        }
      />
      <Route path="/join/:memberId" element={<EmployeeOnboard />} />
      {/* بوابة الموظف — one public portal for every company's employees.
          Outside ProtectedRoute on purpose: employees sign in with an email
          code, not a CardPass account. */}
      <Route path="/portal" element={<EmployeePortal />} />
      {V2Styleguide && <Route path="/__v2/*" element={<V2Styleguide />} />}
      {SCHED_ON && (
        <>
          {SCHED_PROTO ? <Route path="/prototype/scheduling/*" element={<SchedulingPrototype />} /> : null}
          <Route
            path="/app/scheduling/:view?"
            element={
              <ProtectedRoute>
                <IndividualScheduling />
              </ProtectedRoute>
            }
          />
          <Route path="/booking/:token" element={<BookingManage />} />
          <Route path="/team/:company/:team" element={<TeamBookingPublic />} />
          <Route path="/:slug/book/:typeId?" element={<BookingPublic />} />
          <Route path="/embed/team/:company/:team" element={<EmbedBooking team />} />
          <Route path="/embed/:slug/book/:typeId?" element={<EmbedBooking />} />
        </>
      )}
      <Route path="/c/:slug" element={<PublicCard />} />
      <Route path="/:slug" element={<PublicCard />} />
      {/* multi-segment unknown paths — the card-page function already served
          this URL with a 404 status; this is the matching UI */}
      <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </>
  );
}
